import { type JSX, useMemo, useState } from 'react';
import { generatePath } from 'react-router';

import { ClientDetailsApi } from 'Api/Client/ClientDetailsApi';
import { GenerativeAiDisclaimer } from 'Components/ArtificialIntelligence/GenerativeAiDisclaimer';
import { Button } from 'Components/Buttons/Buttons';
import { Switch } from 'Components/Buttons/Switch';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { RBACComponent } from 'Components/Context/RBACComponent';
import { Role } from 'Components/Context/RBACContext';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Text } from 'Components/Text/Text';
import { UnsavedChangesWarning } from 'Components/Text/UnsavedChangesWarning';
import { TextToast } from 'Components/Toast/Toast';
import { SALES_EMAIL_ADDRESS } from 'Config/HPSEmailAddresses';
import { ICON_SAVE } from 'Config/Icons';
import { SETTINGS } from 'Config/PathTemplates';
import { RequestState } from 'Helpers/RequestUtils';
import { useUnsavedChangesTracking } from 'Hooks/Forms';
import { AdvancedFeature, getDescriptionForAdvancedFeature } from 'Models/ClientDetailsModel';

import styles from './AdvancedFeaturesToggler.module.css';

export interface AdvancedFeaturesTogglerProps {
    clientDetailsApi: ClientDetailsApi;
}

interface DisplayableFeature {
    description: string;
    enabled: boolean;
    name: AdvancedFeature;
    requiresPurchase: boolean;
}

/** These features are granted and enabled/disabled only by High Peaks Solutions; Clients cannot toggle them (the back-end ignores attempts to do so). */
const FEATURES_CONTROLLED_ONLY_BY_HIGH_PEAKS_SOLUTIONS = [AdvancedFeature.ANALYTICS, AdvancedFeature.UCRM];
/** These features can be toggled by Clients, but are hidden until UCRM can be shown to clients. At that point, remove them and take care of the TODO comments in this component's test file. */
const FEATURES_NOT_YET_SHOWN_TO_CLIENTS = [AdvancedFeature.AI_UCRM_AUTO_ADVANCE, AdvancedFeature.AI_UCRM_USE_CASE_ASSESSMENT];
const ADVANCED_FEATURES_TO_HIDE = [...FEATURES_CONTROLLED_ONLY_BY_HIGH_PEAKS_SOLUTIONS, ...FEATURES_NOT_YET_SHOWN_TO_CLIENTS];

/**
 * Allows the user to view all advanced features, grouped by whether the Client has been granted the features.
 * Features that have been granted (purchased) can be enabled or disabled; we indicate that the user should contact us to enable the other features.
 */
export const AdvancedFeaturesToggler = (props: AdvancedFeaturesTogglerProps): JSX.Element => {
    const cachedData = useCachedData();

    const displayableFeatures: DisplayableFeature[] = useMemo(() => {
        const grantedFeatures: DisplayableFeature[] = Object.entries(cachedData.clientDetails.advanced_features)
            .filter(([feature]) => !ADVANCED_FEATURES_TO_HIDE.includes(feature as AdvancedFeature))
            .map(([feature, enabled]) => ({
                description: getDescriptionForAdvancedFeature(feature as AdvancedFeature),
                enabled,
                name: feature as AdvancedFeature,
                requiresPurchase: false,
            }))
            .sort((a, b) => a.name.localeCompare(b.name));

        const notGrantedFeatures: DisplayableFeature[] = Object.values(AdvancedFeature)
            .filter((feature) => !ADVANCED_FEATURES_TO_HIDE.includes(feature))
            .filter((feature) => !(feature in cachedData.clientDetails.advanced_features))
            .map((feature) => ({
                description: getDescriptionForAdvancedFeature(feature),
                enabled: false,
                name: feature,
                requiresPurchase: true,
            }))
            .sort((a, b) => a.name.localeCompare(b.name));

        return [...grantedFeatures, ...notGrantedFeatures];
    }, [cachedData.clientDetails]);

    const atLeastOneFeatureCanBeToggled = displayableFeatures.some((feature) => !feature.requiresPurchase);

    const [requestState, setRequestState] = useState<RequestState>({ type: 'idle' });
    const [featureToggleStates, setFeatureToggleStates] = useState<Partial<Record<AdvancedFeature, boolean>>>({ ...cachedData.clientDetails.advanced_features });
    const [hasUnsavedChanges, onFeatureTogglesSaved] = useUnsavedChangesTracking(featureToggleStates, false);

    const toggleFeature = (feature: AdvancedFeature, enabled: boolean): void => {
        setFeatureToggleStates((current) => ({ ...current, [feature]: enabled }));
        if (feature === AdvancedFeature.AI_TPRM_SERVICE_ASSESSMENT && !enabled) {
            setFeatureToggleStates((current) => ({ ...current, [AdvancedFeature.AI_TPRM_AUTO_ADVANCE]: enabled }));
        } else if (feature === AdvancedFeature.AI_TPRM_AUTO_ADVANCE && enabled) {
            setFeatureToggleStates((current) => ({ ...current, [AdvancedFeature.AI_TPRM_SERVICE_ASSESSMENT]: enabled }));
        } else if (feature === AdvancedFeature.AI_UCRM_USE_CASE_ASSESSMENT && !enabled) {
            setFeatureToggleStates((current) => ({ ...current, [AdvancedFeature.AI_UCRM_AUTO_ADVANCE]: enabled }));
        } else if (feature === AdvancedFeature.AI_UCRM_AUTO_ADVANCE && enabled) {
            setFeatureToggleStates((current) => ({ ...current, [AdvancedFeature.AI_UCRM_USE_CASE_ASSESSMENT]: enabled }));
        }
    };

    const handleSubmit = async (): Promise<void> => {
        setRequestState({ type: 'inProgress' });

        try {
            const response = await props.clientDetailsApi.toggleAdvancedFeatures({ advanced_features: featureToggleStates });
            cachedData.refreshClientDetails(response.data);
            onFeatureTogglesSaved(featureToggleStates);
            setRequestState({ type: 'success', message: 'Changes saved.' });
        } catch (error) {
            setRequestState({ type: 'failure', message: error.message });
        }
    };

    return (
        <RBACComponent roles={[Role.ADMIN]}>
            {requestState.type === 'success' && <TextToast variant="success" clearToast={() => setRequestState({ type: 'idle' })} text={requestState.message} />}
            {requestState.type === 'failure' && <TextToast variant="failure" clearToast={() => setRequestState({ type: 'idle' })} text={requestState.message} />}
            <PageLayoutDefault
                headerBreadcrumb={
                    <Breadcrumb textColor="blue">
                        <BreadcrumbLink link={generatePath(SETTINGS)}>Settings</BreadcrumbLink>
                        <BreadcrumbText>Advanced Features</BreadcrumbText>
                    </Breadcrumb>
                }
                headerTitle="Advanced Features"
                headerDescription="Enable or disable advanced features in SummIT."
                headerButtons={
                    atLeastOneFeatureCanBeToggled ? (
                        <>
                            <UnsavedChangesWarning hasUnsavedChanges={hasUnsavedChanges} />
                            <Button variant="primary" fontAwesomeImage={ICON_SAVE} onClick={handleSubmit} isLoading={requestState.type === 'inProgress'} loadingText="Saving...">
                                Save
                            </Button>
                        </>
                    ) : undefined
                }
                body={[
                    {
                        title: 'Advanced Features',
                        content: displayableFeatures.map((feature) => (
                            <div key={feature.name} className={`${styles.featureRow} ${feature.requiresPurchase && atLeastOneFeatureCanBeToggled ? styles.leftPaddingMatchingSwitches : ''}`}>
                                <AdvancedFeatureRow feature={feature} toggledOn={featureToggleStates[feature.name] === true} onChange={(enabled) => toggleFeature(feature.name, enabled)} />
                            </div>
                        )),
                    },
                    {
                        title: 'SummIT Guide™',
                        content: (
                            <>
                                <Text>SummIT Guide™ is an artificial intelligence (AI) enhancement to SummIT, improving efficiency and providing new insights.</Text>
                                <GenerativeAiDisclaimer />
                            </>
                        ),
                    },
                ]}
            />
        </RBACComponent>
    );
};

interface AdvancedFeatureRowProps {
    feature: DisplayableFeature;
    onChange: (enabled: boolean) => void;
    toggledOn: boolean;
}

const AdvancedFeatureRow = (props: AdvancedFeatureRowProps): JSX.Element => {
    return (
        <>
            <div className={styles.toggleAndName}>
                {!props.feature.requiresPurchase && <Switch checked={props.toggledOn} name={props.feature.name} onChange={(_event, checked) => props.onChange(checked)} />}
                <Text variant="Text2" noStyles>
                    {props.feature.name}
                </Text>
            </div>
            <div className={props.feature.requiresPurchase ? undefined : styles.leftPaddingMatchingSwitches}>
                <Text noStyles>{props.feature.description}</Text>
                {props.feature.requiresPurchase && (
                    <Text color="darkGray">
                        If you would like to enable this feature, contact <b>{SALES_EMAIL_ADDRESS}</b>.
                    </Text>
                )}
            </div>
        </>
    );
};
